import { useEffect, useState } from 'react'
import { FlexDiv } from '../../components'
import { AreaContainer } from './AreaContainer'
import { axiosInstance, getNumFromURLSegment } from '../../helpers'
import { Lease } from '../../types/lease'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    useTheme,
    Typography,
    Box,
    Chip,
} from '@mui/material'
import { useAnimationQueue, useAppDispatch } from '../../hooks'
import { RemoveListItem } from '../../animations'
import {
    AreaInspectionStatuses,
    TenantMoveInInspection,
} from '../../types/inspection'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { useLocation, useNavigate } from 'react-router-dom'
import { setLeaseId } from '../../store/User/action'
import { ResponsivePageContainer } from '../../components/PageContainers'
import { MoveInSurveyDialog } from '../Inspections/MoveInSurveyDialog'
import { isMoveInSurveySubmitted, markMoveInSurveySubmitted } from '../../helpers/LocalStorage'

const TENANT_IN = 'TENANT_IN'

export const SelectArea = () => {
    const [leases, setLeases] = useState<Lease[]>([])
    const [renderLeaseList, setRenderLeaseList] = useState<Lease[]>([])
    const [showComplete, setShowComplete] = useState(false)
    const [loading, setLoading] = useState(false)

    const theme = useTheme()
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useAppDispatch()
    const leaseId = useSelector((state: RootState) => state.user.leaseId)

    const [surveyDismissed, setSurveyDismissed] = useState(() => {
        return leaseId != null && isMoveInSurveySubmitted(leaseId)
    })
    const { enqueueAnimation, currentAnimation } = useAnimationQueue()
    interface LeaseAndIdx {
        lease: Lease
        idx: number
    }
    const [leaseComplete, setLeaseComplete] = useState<LeaseAndIdx>()
    const [statusChangeLoading, setStatusChangeLoading] = useState<
        number | null
    >(null)

    useEffect(() => {
        if (leaseId) {
            setLoading(true)
            axiosInstance
                .post('lease/lease-tenant/get_lease_children/', { id: leaseId })
                .then((res) => {
                    const parent: Lease = res.data
                    const leases: Lease[] = [parent, ...(parent.children ?? [])]

                    setLeases(leases)
                })
                .finally(() => {
                    setLoading(false)
                })
        } else {
            // attemp to get from url
            // url.pathname is the part after ezturn.co, for ex: /select-area/1/
            const urlSegment = location.pathname.substring(13) // the characters where the number is and beyond
            const leaseIdNum = getNumFromURLSegment(urlSegment).number

            dispatch(setLeaseId(leaseIdNum))
        }
    }, [leaseId])

    useEffect(() => {
        if (leases.length > 0) {
            const newList: Lease[] = []
            leases.forEach((lease) => {
                const complete =
                    lease.tenant_move_in_inspection?.status ===
                    AreaInspectionStatuses.COMPLETE
                if (complete === showComplete) {
                    newList.push(lease)
                }
            })
            setRenderLeaseList(newList)
        }
    }, [leases, showComplete])

    return (
        <ResponsivePageContainer>
            <FlexDiv
                direction="column"
                style={{
                    maxWidth: '800px',
                    width: '100%',
                    gap: theme.spacing(3),
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        backgroundColor: '#2a2f35',
                        borderRadius: 3,
                        padding: theme.spacing(3),
                        border: `1px solid ${theme.palette.grey[700]}`,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                    }}
                >
                    <Typography
                        variant="h4"
                        style={{
                            color: '#fff',
                            marginBottom: theme.spacing(2),
                            fontWeight: 600,
                        }}
                    >
                        Move-In Inspection
                    </Typography>
                    <Typography
                        variant="body1"
                        style={{
                            color: '#8b949e',
                            marginBottom: theme.spacing(3),
                            lineHeight: 1.6,
                        }}
                    >
                        Select an area to inspect and document any issues you find.
                    </Typography>

                    {/* Toggle Button */}
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setShowComplete(!showComplete)
                        }}
                        sx={{
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            textTransform: 'none',
                            fontWeight: 600,
                            padding: theme.spacing(1, 3),
                            borderRadius: 2,
                            '&:hover': {
                                borderColor: theme.palette.primary.dark,
                                backgroundColor: 'rgba(0, 140, 133, 0.1)',
                            }
                        }}
                    >
                        {showComplete
                            ? 'Show Incomplete Areas'
                            : 'Show Completed Areas'}
                    </Button>
                </Box>

                {/* Area Containers */}
                <FlexDiv
                    direction="column"
                    style={{
                        gap: theme.spacing(2),
                    }}
                >
                    {renderLeaseList.map((lease, idx) => {
                        return (
                            <AreaContainer
                                loading={statusChangeLoading === lease.id}
                                lease={lease}
                                key={`AREA_CONTAINER_${lease.id}`}
                                updateLease={(updatedLease: Lease) => {
                                    const newLeases = leases.map(
                                        (innerLease: Lease) => {
                                            if (innerLease.id === updatedLease.id) {
                                                return updatedLease
                                            }
                                            return innerLease
                                        },
                                    )
                                    setLeases(newLeases)
                                }}
                                currentAnimation={currentAnimation}
                                enqueueAnimation={enqueueAnimation}
                                onStatusChange={() => {
                                    setLeaseComplete({ lease: lease, idx: idx })
                                }}
                            />
                        )
                    })}
                </FlexDiv>

                {/* Loading State */}
                {loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: theme.spacing(6),
                            backgroundColor: '#2a2f35',
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.grey[700]}`,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        }}
                    >
                        <CircularProgress
                            size={80}
                            sx={{ color: theme.palette.primary.main }}
                        />
                    </Box>
                )}

                {/* Empty State */}
                {renderLeaseList.length === 0 && !showComplete && !loading && (
                    <Box
                        sx={{
                            backgroundColor: '#2a2f35',
                            borderRadius: 3,
                            padding: theme.spacing(4),
                            textAlign: 'center',
                            border: `1px solid ${theme.palette.grey[700]}`,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        }}
                    >
                        <Typography
                            variant="h6"
                            style={{
                                color: '#fff',
                                marginBottom: theme.spacing(2),
                                fontWeight: 600,
                            }}
                        >
                            All Inspections Complete!
                        </Typography>
                        <Typography
                            variant="body1"
                            style={{
                                color: '#8b949e',
                                marginBottom: theme.spacing(3),
                                lineHeight: 1.6,
                            }}
                        >
                            You have successfully completed all move-in inspections.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => {
                                navigate('../')
                            }}
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '1rem',
                                padding: theme.spacing(1.5, 3),
                                borderRadius: 2,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                }
                            }}
                        >
                            Return to Home
                        </Button>
                    </Box>
                )}

                {/* Move-In Survey */}
                {renderLeaseList.length === 0 && !showComplete && !loading && leaseId != null && !surveyDismissed && (
                    <MoveInSurveyDialog
                        open
                        leaseId={leaseId}
                        onClose={() => setSurveyDismissed(true)}
                        onSuccess={() => {
                            markMoveInSurveySubmitted(leaseId)
                            setSurveyDismissed(true)
                        }}
                    />
                )}

                {/* Confirmation Dialog */}
                <Dialog
                    open={leaseComplete !== undefined}
                    onClose={() => {
                        setLeaseComplete(undefined)
                    }}
                    PaperProps={{
                        sx: {
                            backgroundColor: '#2a2f35',
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.grey[700]}`,
                            maxWidth: '500px',
                            width: '90%',
                        }
                    }}
                >
                    <DialogContent sx={{ padding: theme.spacing(3) }}>
                        <Typography
                            variant="h6"
                            style={{
                                color: '#fff',
                                marginBottom: theme.spacing(2),
                                fontWeight: 600,
                            }}
                        >
                            Complete Inspection
                        </Typography>
                        <Typography
                            variant="body1"
                            style={{
                                color: '#8b949e',
                                lineHeight: 1.6,
                            }}
                        >
                            Are you sure you want to complete your inspection? Once
                            completed, you can no longer add any additional issues.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ padding: theme.spacing(3), paddingTop: 0 }}>
                        <Button
                            onClick={() => {
                                setLeaseComplete(undefined)
                            }}
                            sx={{
                                color: '#8b949e',
                                textTransform: 'none',
                                fontWeight: 600,
                                padding: theme.spacing(1, 2),
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (leaseComplete) {
                                    setStatusChangeLoading(leaseComplete.lease.id)
                                    axiosInstance
                                        .post(
                                            'inspection/area-inspection/bulk_transition/',
                                            {
                                                area_inspections: [
                                                    leaseComplete.lease
                                                        .tenant_move_in_inspection
                                                        ?.id,
                                                ],
                                                to_status:
                                                    AreaInspectionStatuses.COMPLETE,
                                                is_tenant: true,
                                            },
                                        )
                                        .then((res) => {
                                            setStatusChangeLoading(null)
                                            const updatedLease: Lease = {
                                                ...leaseComplete.lease,
                                                tenant_move_in_inspection: {
                                                    ...(leaseComplete.lease
                                                        .tenant_move_in_inspection as TenantMoveInInspection),
                                                    status: 'COMPLETE',
                                                },
                                            }
                                            enqueueAnimation(
                                                RemoveListItem(
                                                    250,
                                                    leaseComplete.lease,
                                                    renderLeaseList.slice(
                                                        leaseComplete.idx + 1,
                                                    ),
                                                    100,
                                                    () => {
                                                        const newLeases =
                                                            leases.map(
                                                                (
                                                                    innerLease: Lease,
                                                                ) => {
                                                                    if (
                                                                        innerLease.id ===
                                                                        updatedLease.id
                                                                    ) {
                                                                        return updatedLease
                                                                    }
                                                                    return innerLease
                                                                },
                                                            )
                                                        setLeases(newLeases)
                                                    },
                                                ),
                                            )
                                            setLeaseComplete(undefined)
                                        })
                                }
                            }}
                            variant="contained"
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                textTransform: 'none',
                                fontWeight: 600,
                                padding: theme.spacing(1, 3),
                                borderRadius: 2,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                }
                            }}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            </FlexDiv>
        </ResponsivePageContainer>
    )
}
